import {DatedCycle, expandRecurrence, expandRecurrenceCycles, TaskSetFormState} from './useTaskSetPreview';

/**
 * A count typed into a field: a whole number, null for an empty field, and the
 * current value for anything else, so a stray keystroke does not wipe it.
 */
export const countFrom = (entered: string, current: number | null): number | null => {
	if (!entered.trim()) {
		return null;
	}
	const parsed = Number(entered);
	return Number.isInteger(parsed) && parsed >= 0 ? parsed : current;
};

/**
 * Which repeat of the pattern (from 1) a task starting on `startDate` belongs
 * to: the repeat of the latest rule date on or before it. A task dragged off
 * the pattern stays in the repeat it was dragged into; one before the first
 * date is in repeat 1.
 */
export const repeatOf = (dated: readonly DatedCycle[], startDate: string): number => {
	let cycle = 0;
	for (const entry of dated) {
		if (entry.date > startDate) break;
		cycle = entry.cycle;
	}
	return cycle + 1;
};

export interface CountEndsOption<T> {
	/** The task the option names: the latest existing one in its repeat. */
	instance: T;
	/** How many times the set repeats if it ends here. */
	count: number;
	/** The last date the pattern produces in this repeat. */
	repeatEnd: string;
	/**
	 * Where the set ends if this is chosen: the repeat's last pattern date, or
	 * the task's own start when it was dragged later than that. Everything up
	 * to here is kept -- the same date the server keeps through.
	 */
	end: string;
	/** How many existing tasks start after `end` and would be deleted. */
	removed: number;
}

/**
 * One Ends option per repeat that still has a task, so every option is a whole
 * number of times. On a weekly set with several days this skips every task but
 * the last of each week, which is what stops a set being cut part way through
 * a repeat.
 */
export const countEndsOptions = <T extends {startDate?: string | null}>(
	instances: readonly T[],
	dated: readonly DatedCycle[]
): CountEndsOption<T>[] => {
	const latestByRepeat = new Map<number, T>();
	instances.forEach(instance => {
		if (!instance.startDate) return;
		const repeat = repeatOf(dated, instance.startDate);
		const current = latestByRepeat.get(repeat);
		if (!current || (current.startDate ?? '') < instance.startDate) {
			latestByRepeat.set(repeat, instance);
		}
	});

	return Array.from(latestByRepeat.entries())
		.sort(([a], [b]) => a - b)
		.map(([count, instance]) => {
			const start = instance.startDate as string;
			const repeatEnd = dated.filter(entry => entry.cycle === count - 1).pop()?.date ?? start;
			// The instance is the latest in its repeat, and repeats are ordered by
			// date, so no task of an earlier repeat starts after it.
			const end = start > repeatEnd ? start : repeatEnd;
			return {
				instance,
				count,
				repeatEnd,
				end,
				removed: instances.filter(other => !!other.startDate && other.startDate > end).length,
			};
		});
};

/** The last date of `count` repeats of the pattern, or null when it makes none. */
export const lastDateForCount = (
	pattern: TaskSetFormState,
	count: number,
	isWorkingDay?: (iso: string) => boolean
): string | null =>
	expandRecurrence({...pattern, boundType: 'COUNT', boundCount: count, boundEndDate: null}, isWorkingDay).pop() ?? null;

/** Every date of `count` repeats, for walking options and counting what a raise adds. */
export const datedForCount = (
	pattern: TaskSetFormState,
	count: number,
	isWorkingDay?: (iso: string) => boolean
): DatedCycle[] =>
	expandRecurrenceCycles({...pattern, boundType: 'COUNT', boundCount: count, boundEndDate: null}, isWorkingDay);
